/**
 * Getting into a room: pick a signaling strategy, claim a role, and decide when the session is
 * real enough to hand to the game.
 *
 * A room that has been opened is not yet a session. A host has to wait out its claim window to know
 * nobody else holds the code, and a guest has to actually meet the host. Both of those are what
 * "settle" means to the fallback runner, so a relay that accepts a socket and then swallows
 * everything published to it fails here and the next strategy is tried, instead of leaving a player
 * looking at a lobby nobody can join.
 *
 * The claim state created here outlives this file: the transport keeps reading it, because a peer
 * arriving an hour into a session is judged by the same table as the first one.
 */

import { NET_APP_ID, roomIdForCode, roomPasswordForCode } from '@/net/session/roomIdentity';
import { NetError, asNetError } from '@/net/session/sessionErrors';
import { createPeerHandshake } from '@/net/session/peerHandshake';
import { MAX_GUESTS } from '@/net/session/sessionLimits';
import { NO_SEAT_PROOF, claimsHeldSeat, proveSeat } from '@/net/wire/seatProof';
import { rtcConfiguration } from '@/net/rtc/iceConfig';
import { runWithFallback } from '@/net/signaling/strategyFallback';
import type { FallbackBudget, StrategyAttempt } from '@/net/signaling/strategyFallback';
import type { HandshakeDecision } from '@/net/session/sessionRole';
import type { Room } from 'trystero';
import type { RoleClaim } from '@/net/wire/helloFrame';
import type { SessionRole } from '@/net/transportContract';
import type { SignalingStrategy } from '@/net/signaling/signalingContract';

/**
 * How long a host waits before it believes the code is its own. Longer than a relay round trip,
 * short enough that starting a game does not feel like waiting. A heuristic, not a lock: two hosts
 * whose announces never reach each other cannot be reconciled without a server.
 */
export const DEFAULT_HOST_CLAIM_MS = 2500;

/** Enough relays to survive a few dead ones without announcing to dozens of strangers. */
const RELAY_REDUNDANCY = 4;

export interface ClaimState {
  claim: RoleClaim;
  /** When this side started claiming the code, for the collision tie-break. */
  readonly claimedAt: number;
  /** Set when this side lost a collision and has to give the code up. */
  yielded: boolean;
  hostPeerId: string | null;
  readonly peerRoles: Map<string, SessionRole>;
  /** Peers this side refused, so Trystero's error callback can tell ours from its own. */
  readonly refusals: Map<string, NetError>;
  /** A failure that ends the attempt now rather than at the end of the budget. */
  fatal: NetError | null;
}

export interface JoinRequest {
  readonly role: SessionRole;
  /** Canonical, never formatted. Generated by the caller when hosting. */
  readonly code: string;
  readonly strategies: readonly SignalingStrategy[];
  readonly budget: FallbackBudget;
  readonly hostClaimMs: number;
  /**
   * The tokens of the seats being kept for players who dropped. They have no peer here and still
   * occupy the session, and one of them may belong to the guest currently at the door.
   */
  readonly heldTokens: () => readonly string[];
  /** What this side was seated with here before, or null. A host never has one. */
  readonly rejoinToken: () => string | null;
  readonly onAttempt: (attempt: StrategyAttempt) => void;
  /** Errors Trystero raised that this side did not ask for. */
  readonly onIssue: (error: NetError) => void;
  /**
   * A failure that arrives after the session was already live, which is how a late collision shows
   * up: two hosts on slow relays can both settle before either hears the other.
   */
  readonly onLateFailure: (error: NetError) => void;
}

export interface JoinedRoom {
  readonly room: Room;
  readonly strategyId: string;
  /** This tab's peer id, taken from the strategy that actually carried the session. */
  readonly selfId: string;
  readonly state: ClaimState;
  readonly attempts: readonly StrategyAttempt[];
}

export async function joinRoomWithFallback(
  request: JoinRequest,
  signal: AbortSignal,
): Promise<JoinedRoom> {
  const roomId = await roomIdForCode(request.code);
  const state: ClaimState = {
    claim: request.role === 'host' ? 'hostProbing' : 'guest',
    claimedAt: performance.now(),
    yielded: false,
    hostPeerId: null,
    peerRoles: new Map<string, SessionRole>(),
    refusals: new Map<string, NetError>(),
    fatal: null,
  };

  // Before the session settles this wakes the waiter below. After it settles there is nothing left
  // waiting, so a refusal that yields the code becomes a late failure instead.
  let wake: () => void = (): void => {
    if (state.fatal !== null) request.onLateFailure(state.fatal);
  };
  const poke = (): void => {
    wake();
  };

  const handshake = createPeerHandshake({
    claim: (): RoleClaim => state.claim,
    hasHost: (): boolean => state.hostPeerId !== null,
    heldMs: (): number => performance.now() - state.claimedAt,
    nonce: (): number => crypto.getRandomValues(new Uint32Array(1))[0] ?? 0,
    freeSeats: (claimedByPeer: boolean): number =>
      request.role === 'host' ? freeSeats(state, request.heldTokens().length, claimedByPeer) : 0,
    // A guest proves the seat it left is still its own, over the nonce it is about to send. A host
    // claims nothing: it never left, and it holds the tokens rather than presenting one.
    seatProof: async (nonce: number): Promise<Uint8Array> => {
      const token = request.role === 'guest' ? request.rejoinToken() : null;
      return token === null ? NO_SEAT_PROOF : await proveSeat(nonce, token);
    },
    // Only the host can answer this, because only the host is keeping seats.
    claimsHeldSeat: async (nonce: number, proof: Uint8Array): Promise<boolean> =>
      request.role === 'host' && (await claimsHeldSeat(nonce, proof, request.heldTokens())),
    onAccepted: (peerId: string, peerRole: SessionRole): void => {
      state.peerRoles.set(peerId, peerRole);
      if (peerRole === 'host') state.hostPeerId = peerId;
      poke();
    },
    onRefused: (peerId: string, decision: Extract<HandshakeDecision, { accept: false }>): void => {
      const error = new NetError(decision.code, decision.detail);
      state.refusals.set(peerId, error);
      if (decision.yieldSession) {
        state.yielded = true;
        state.fatal = error;
      }
      poke();
    },
  });

  const result = await runWithFallback(
    request.strategies,
    {
      open: async (strategy: SignalingStrategy): Promise<Room> =>
        strategy.join(
          {
            appId: NET_APP_ID,
            password: roomPasswordForCode(request.code),
            rtcConfig: rtcConfiguration(),
            relayConfig: { redundancy: RELAY_REDUNDANCY },
          },
          roomId,
          {
            onPeerHandshake: handshake,
            onJoinError: ({ peerId, error }): void => {
              // Our own refusals arrive here too, and a refused guest-to-guest pair is the star
              // working, not a failure worth showing anyone.
              if (state.refusals.has(peerId)) return;
              request.onIssue(new NetError('peerConnectionFailed', error));
            },
          },
        ),
      settle: (_room: Room, settleSignal: AbortSignal): Promise<void> =>
        settleRoom(request, state, settleSignal, (next) => {
          wake = next;
        }),
      close: (room: Room): Promise<void> => leaveQuietly(room),
    },
    request.budget,
    request.onAttempt,
    signal,
  );

  return {
    room: result.opened,
    strategyId: result.strategy.id,
    selfId: await result.strategy.selfId(),
    state,
    attempts: result.attempts,
  };
}

/**
 * Chairs the host can still offer, counted the moment a handshake asks.
 *
 * From the roles this table has accepted rather than from the transport's peer list, because a peer
 * is announced some time after it is accepted here and four handshakes running at once would
 * otherwise all read four empty chairs and all be let in.
 *
 * A held seat is taken, except by the one player who can prove it is theirs: `claimedByPeer` is
 * that proof having landed, and it is what a returning guest is let back in through when the
 * session was full at the moment it dropped.
 */
function freeSeats(state: ClaimState, held: number, claimedByPeer: boolean): number {
  let guests = 0;
  for (const role of state.peerRoles.values()) if (role === 'guest') guests += 1;
  const kept = claimedByPeer ? Math.max(held - 1, 0) : held;
  return Math.max(MAX_GUESTS - guests - kept, 0);
}

/** Never throws. A room being abandoned has already failed, and a second error helps nobody. */
export async function leaveQuietly(room: Room): Promise<void> {
  try {
    await room.leave();
  } catch {
    // Nothing to do: the room is being dropped either way.
  }
}

/**
 * Resolves once this side believes it has a session. A host waits out its claim window, a guest
 * waits for the host to accept it.
 */
function settleRoom(
  request: JoinRequest,
  state: ClaimState,
  signal: AbortSignal,
  register: (wake: () => void) => void,
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const done = (settle: () => void): void => {
      clearTimeout(timer);
      signal.removeEventListener('abort', onAbort);
      register(reportLate);
      settle();
    };

    /** What replaces the waiter once there is nothing left to wait for. */
    const reportLate = (): void => {
      if (state.fatal !== null) request.onLateFailure(state.fatal);
    };

    const check = (): void => {
      if (state.fatal !== null) {
        const error = state.fatal;
        done(() => {
          reject(error);
        });
        return;
      }
      if (request.role === 'guest' && state.hostPeerId !== null) {
        done(resolve);
      }
    };

    const onAbort = (): void => {
      const error =
        request.role === 'guest'
          ? new NetError('noHostForCode', 'nobody answered on this relay network')
          : asNetError(signal.reason, 'relayUnreachable');
      done(() => {
        reject(error);
      });
    };

    // A host settles on time rather than on an event: silence for the whole window is exactly the
    // evidence that nobody else holds this code.
    const timer =
      request.role === 'host'
        ? setTimeout(() => {
            if (state.fatal !== null) {
              check();
              return;
            }
            state.claim = 'host';
            done(resolve);
          }, request.hostClaimMs)
        : undefined;

    signal.addEventListener('abort', onAbort, { once: true });
    register(check);
    check();
  });
}
